/**
 * UserStateDetailModal — bir kullanıcının görevdeki hesabına giren CH ham
 * satırlarını gösterir. TaskUserStatesModal'da "Detay" ikonuna basılınca açılır.
 *
 * Veri: GET /rule-engine/tasks/{id}/user-states/{kodu}/detail
 * Worker'ın `_build_where` + `_date_filter` mantığı backend'de aynen reuse
 * ediliyor → görünen satır sayısı evaluations.raw_value ile eşleşmeli.
 *
 * Metric_type davranışı:
 *   COUNT/SUM: tek tablo, filtreye uyan satırlar
 *   RATIO:    leg seçici (Pay / Payda), her leg için ayrı SQL
 *   RANK:     COUNT gibi
 *   MANUAL:   CH detayı yok, manuel girilen değer + audit
 */
import { useEffect, useState, useMemo } from 'react';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { Icon } from '../../components/ui/Icon';
import { tasksApi } from '../../services/api';
import { downloadBlob } from '../../utils/download';

// Sayfa boyutu seçenekleri — kullanıcı dropdown'dan seçer.
// 1000 üst sınır backend Query(le=1000) ile uyumlu.
const LIMIT_OPTIONS = [50, 100, 200, 500, 1000];
const DEFAULT_LIMIT = 200;

export default function UserStateDetailModal({ open, onClose, task, user }) {
  const [leg, setLeg]         = useState('numerator');
  // mode: 'include' (Dahil — sayılan satırlar) ya da 'exclude' (Hariç —
  // worker hesabında çıkarılan satırlar). Default 'include'.
  const [mode, setMode]       = useState('include');
  const [limit, setLimit]     = useState(DEFAULT_LIMIT);
  // 0-indexed offset (CH OFFSET ile birebir). Sayfa = offset/limit + 1.
  const [offset, setOffset]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError]     = useState(null);
  const [data, setData]       = useState(null);

  const isRatio = task?.metric_type === 'RATIO';
  const isManual = task?.metric_type === 'MANUAL';

  // CH ham veriyi Excel indir — görüntülenen leg/mode ile tutarlı; sayfalama
  // yok sayılır, filtreye uyan TÜM satırlar iner. Backend: detail?export=excel.
  const handleExport = async () => {
    if (!task?.id || !user?.kullanici_kodu || exporting) return;
    setExporting(true);
    setError(null);
    try {
      const params = { mode };
      if (isRatio) params.leg = leg;
      const res = await tasksApi.exportUserStateDetail(
        task.id, user.kullanici_kodu, params,
      );
      const safeUser = String(user.kullanici_kodu).replace(/[/\\]/g, '_');
      downloadBlob(res.data ?? res, `gorev_${task.id}_${safeUser}_ham_veri.xlsx`);
    } catch (err) {
      setError(err?.message || 'Excel indirilemedi.');
    } finally {
      setExporting(false);
    }
  };

  // Veri fetch — limit/offset/leg/mode değişince yeniden çek.
  useEffect(() => {
    if (!open || !task?.id || !user?.kullanici_kodu) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    // NOT: setData(null) yapmıyoruz — sayfa geçişinde önceki sayfa veri-
    // çatkısı (kolonlar) flicker etmesin diye. Loading spinner ayrı gösteriyor.

    const params = { limit, offset, mode };
    if (isRatio) params.leg = leg;

    tasksApi
      .userStateDetail(task.id, user.kullanici_kodu, params)
      .then((res) => {
        if (cancelled) return;
        setData(res.data || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Detay yüklenemedi.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [open, task?.id, user?.kullanici_kodu, leg, isRatio, limit, offset, mode]);

  // Modal kapanınca state'i resetle. Aynı kullanıcı tekrar açılırsa veri
  // güncel gelsin diye `data`'yı da temizliyoruz.
  useEffect(() => {
    if (!open) {
      setLeg('numerator');
      setMode('include');
      setOffset(0);
      setLimit(DEFAULT_LIMIT);
      setData(null);
    }
  }, [open]);

  // Leg, limit veya mode değişince offset'i sıfırla — yeni filtrede 1. sayfaya dön.
  useEffect(() => {
    setOffset(0);
  }, [leg, limit, mode]);

  // Pagination meta — yeni backend `pagination` bloğu döner; eski backend
  // sadece `summary.truncated` ile "daha var" sinyalini verir. Üç katmanlı
  // fallback:
  //   1. Yeni backend: data.pagination.{total,page,pages} — kesin sayfa sayısı
  //   2. Eski backend + truncated=true: total bilinmiyor → "?"
  //      sayfa "X / ?" şeklinde, Sonraki butonu aktif
  //   3. Eski backend + truncated=false: total = shown_rows + offset (kesin)
  const pagination = useMemo(() => {
    // 1. Yeni backend — kullanım kolay
    if (data?.pagination) return { ...data.pagination, unknownTotal: false };

    // 2-3. Eski backend fallback
    const shown = data?.rows?.length ?? 0;
    const truncated = Boolean(data?.summary?.truncated);
    if (truncated) {
      // Total bilinmiyor — Sonraki var ama kaç sayfa olduğu meçhul
      return {
        total: offset + shown,        // alt sınır
        unknownTotal: true,           // UI "?" göstersin
        limit,
        offset,
        page: Math.floor(offset / limit) + 1,
        pages: null,                  // bilinmiyor
      };
    }
    // Truncated değil → bu son sayfa
    const total = offset + shown;
    return {
      total,
      unknownTotal: false,
      limit,
      offset,
      page: Math.floor(offset / limit) + 1,
      pages: Math.max(1, Math.ceil(total / Math.max(1, limit))),
    };
  }, [data, limit, offset]);

  // Önceki: offset > 0 ise her zaman gidilebilir
  const canPrev = !loading && offset > 0;
  // Sonraki: ya bilinen total daha büyük, ya da unknown total + truncated true
  const canNext = !loading && data && (
    pagination.unknownTotal
      ? Boolean(data.summary?.truncated)
      : (offset + (data.rows?.length || 0)) < (pagination.total || 0)
  );

  // Paging UI'nin görünür olması için: ya başka sayfaya gidilebilir
  // (canPrev || canNext), ya da total > limit (yeni backend'de net biliniyor).
  // Bu kontrol footer'da `pagination.total > limit` yerine kullanılacak.
  const showPagingControls = canPrev || canNext;

  const goPrev = () => setOffset((o) => Math.max(0, o - limit));
  const goNext = () => setOffset((o) => o + limit);

  const fmtCell = (val) => {
    if (val == null) return <span className="text-neutral-300">—</span>;
    if (typeof val === 'number') {
      return val.toLocaleString('tr-TR', { maximumFractionDigits: 4 });
    }
    return String(val);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2 min-w-0">
          <Icon name="search" className="w-4 h-4 text-primary-600 flex-shrink-0" />
          <span className="whitespace-nowrap">Hesaplama Detayı</span>
          {user?.kullanici_kodu && (
            <span className="text-sm text-neutral-500 font-normal truncate min-w-0">
              — {user.kullanici_kodu}
              {user.kullanici_adsoyad && (
                <span className="ml-1 text-neutral-400">({user.kullanici_adsoyad})</span>
              )}
            </span>
          )}
        </div>
      }
      size="xl"
      footer={
        <div className="flex items-center justify-between w-full gap-3 flex-wrap">
          {/* Sol blok — bu sayfada görüntülenen aralık + sayfa boyutu seçici.
            * Toplam kayıt sayısı tablonun ÜSTÜNDE "Toplam: N kayıt" şeklinde
            * gösteriliyor — burada tekrar etmiyoruz, sadece "şu an gösterilen"
            * aralığı belirtiyoruz. */}
          <div className="flex items-center gap-3 text-xs text-neutral-600">
            {!isManual && data?.summary && (data.rows?.length ?? 0) > 0 && (
              <span>
                <span className="text-neutral-500">Bu sayfa: </span>
                <span className="font-medium text-neutral-800 tabular-nums">
                  {offset + 1}
                  {data.rows.length > 1 && `–${offset + data.rows.length}`}
                </span>
              </span>
            )}
            {!isManual && (
              <label className="flex items-center gap-1.5">
                <span className="text-neutral-500">Boyut:</span>
                <select
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value))}
                  disabled={loading}
                  className="text-xs border border-neutral-200 rounded px-1.5 py-0.5 bg-white hover:border-neutral-300 focus:outline-none focus:ring-1 focus:ring-primary-400 disabled:opacity-50"
                >
                  {LIMIT_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {/* Sağ blok — Önceki/Sonraki + Kapat
            * showPagingControls: canPrev || canNext — yani gidilebilecek bir
            * yer varsa butonları göster. Yeni backend: kesin total ile karşılaştırır.
            * Eski backend (truncated): Sonraki aktif, sayfa sayısı bilinmiyor.
            * Sayfa "X / Y" göstergesi de tablonun üstünde olduğu için burada
            * tekrar etmiyoruz, sadece butonlar var. */}
          <div className="flex items-center gap-2">
            {!isManual && showPagingControls && (
              <>
                <Button
                  variant="secondary"
                  icon="chevron-left"
                  disabled={!canPrev}
                  onClick={goPrev}
                >
                  Önceki
                </Button>
                <Button
                  variant="secondary"
                  icon="chevron-right"
                  disabled={!canNext}
                  onClick={goNext}
                >
                  Sonraki
                </Button>
              </>
            )}
            {!isManual && data && (
              <Button
                variant="secondary"
                icon="download"
                onClick={handleExport}
                disabled={exporting || loading}
                title="Filtreye uyan tüm ham satırları Excel olarak indir"
              >
                {exporting ? 'İndiriliyor…' : 'İndir'}
              </Button>
            )}
            <Button variant="primary" onClick={onClose}>Kapat</Button>
          </div>
        </div>
      }
    >
      {/* MANUAL görev — özel mesaj */}
      {isManual && data && (
        <div className="p-4 rounded-lg bg-violet-50 border border-violet-200">
          <p className="text-sm font-medium text-violet-900 mb-2">
            <Icon name="info" className="inline w-4 h-4 mr-1 align-text-bottom" />
            Bu MANUAL bir görevdir — CH kaynak verisi yoktur.
          </p>
          <p className="text-xs text-violet-700 mb-3">
            Admin tarafından manuel olarak girilen değer:
          </p>
          {data.manual_value ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Field label="Ham Değer" value={data.manual_value.raw_value} />
              <Field label="Puan"      value={data.manual_value.points} />
              <Field label="Bonus"     value={data.manual_value.bonus_points} />
              <Field label="Toplam"    value={data.manual_value.total_points} />
            </div>
          ) : (
            <p className="text-xs text-neutral-500 italic">Henüz manuel değer girilmemiş.</p>
          )}
        </div>
      )}

      {/* RATIO görev — leg seçici (Pay / Payda) */}
      {isRatio && !isManual && (
        <div className="mb-3">
          <div className="inline-flex rounded-lg border border-neutral-200 bg-white p-1">
            <button
              type="button"
              onClick={() => setLeg('numerator')}
              className={`px-3 py-1 text-xs rounded-md transition-colors ${
                leg === 'numerator'
                  ? 'bg-primary-500 text-white shadow-sm'
                  : 'text-neutral-600 hover:bg-neutral-50'
              }`}
            >
              Pay (Numerator)
            </button>
            <button
              type="button"
              onClick={() => setLeg('denominator')}
              className={`px-3 py-1 text-xs rounded-md transition-colors ${
                leg === 'denominator'
                  ? 'bg-primary-500 text-white shadow-sm'
                  : 'text-neutral-600 hover:bg-neutral-50'
              }`}
            >
              Payda (Denominator)
            </button>
          </div>
          <p className="text-[11px] text-neutral-500 mt-1">
            RATIO görevde pay/payda için ayrı filtreler uygulanır. İki tarafı
            ayrı incelemek için sekme değiştirin.
          </p>
        </div>
      )}

      {/* Dahil / Hariç mode seçici — yalnızca hariç filtresi varsa görünür.
        * Worker raw_value = COUNT(Dahil) - COUNT(Hariç). İki tarafı ayrı
        * incelemek için. has_exclude_filters backend tarafından döner — eski
        * backend cevabında yoksa exclude_filters array'inin uzunluğuna düşeriz. */}
      {!isManual && data && (
        data.has_exclude_filters || (Array.isArray(data.exclude_filters) && data.exclude_filters.length > 0)
      ) && (
        <div className="mb-3">
          <div className="inline-flex rounded-lg border border-neutral-200 bg-white p-1">
            <button
              type="button"
              onClick={() => setMode('include')}
              className={`px-3 py-1 text-xs rounded-md transition-colors flex items-center gap-1.5 ${
                mode === 'include'
                  ? 'bg-emerald-500 text-white shadow-sm'
                  : 'text-neutral-600 hover:bg-neutral-50'
              }`}
            >
              <Icon name="plus-circle" className="w-3.5 h-3.5" />
              Dahil (Sayılan)
            </button>
            <button
              type="button"
              onClick={() => setMode('exclude')}
              className={`px-3 py-1 text-xs rounded-md transition-colors flex items-center gap-1.5 ${
                mode === 'exclude'
                  ? 'bg-rose-500 text-white shadow-sm'
                  : 'text-neutral-600 hover:bg-neutral-50'
              }`}
            >
              <Icon name="minus-circle" className="w-3.5 h-3.5" />
              Hariç (Çıkarılan)
            </button>
          </div>
          <p className="text-[11px] text-neutral-500 mt-1">
            Worker hesabı: <span className="font-mono">raw_value = COUNT(Dahil) − COUNT(Hariç)</span>.
            Hariç sekmesi worker'ın toplam'dan düştüğü satırları gösterir.
          </p>
        </div>
      )}

      {/* Uygulanan filtre özeti — admin "ne uygulandı" görsün.
        * Eski backend (mode yok) sadece `applied_filters` döner — bu listeyi
        * "Dahil Filtreler" gibi gösteririz. Yeni backend ise `include_filters`
        * + `exclude_filters` ikisini birden döner; bu durumda her iki tarafı
        * ayrı renkli chip'lerle yan yana gösteririz. */}
      {data && !isManual && (
        <div className="mb-3 p-3 rounded-lg bg-neutral-50 border border-neutral-200">
          <p className="text-xs font-semibold text-neutral-700 mb-2 flex items-center gap-1">
            <Icon name="filter" className="w-3.5 h-3.5" />
            Uygulanan Filtreler
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs mb-2">
            <div>
              <span className="text-neutral-500">Kaynak Tablo: </span>
              <span className="font-mono text-neutral-800">{data.source_table || '—'}</span>
            </div>
            <div>
              <span className="text-neutral-500">Tarih Aralığı: </span>
              <span className="text-neutral-800">
                {data.date_range?.start} → {data.date_range?.end}
              </span>
            </div>
          </div>

          {/* Yeni backend: include + exclude ayrı listelerle gelir. */}
          {Array.isArray(data.include_filters) || Array.isArray(data.exclude_filters) ? (
            <div className="space-y-2 text-xs">
              <FilterChipGroup
                label="Dahil Etme Filtreleri"
                icon="plus-circle"
                color="green"
                filters={data.include_filters || []}
                emptyText="Dahil filtresi yok (tüm satırlar sayılır)"
              />
              <FilterChipGroup
                label="Hariç Tutulacak Filtreler"
                icon="minus-circle"
                color="red"
                filters={data.exclude_filters || []}
                emptyText="Hariç filtresi yok"
                hideIfEmpty={false}
              />
            </div>
          ) : (
            /* Eski backend fallback — sadece applied_filters */
            <div className="text-xs">
              <span className="text-neutral-500">Filtreler: </span>
              {Array.isArray(data.applied_filters) && data.applied_filters.length > 0 ? (
                <div className="flex flex-wrap gap-1 mt-1">
                  {data.applied_filters.map((f, i) => (
                    <Badge
                      key={i}
                      color="blue"
                      size="sm"
                      className="!whitespace-normal break-all max-w-full text-left"
                    >
                      <span className="font-mono">
                        {f.column} {f.op} {
                          Array.isArray(f.value)
                            ? `[${f.value.join(', ')}]`
                            : String(f.value)
                        }
                      </span>
                    </Badge>
                  ))}
                </div>
              ) : (
                <span className="text-neutral-400 italic">Filtre yok</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Toplam satır göstergesi — tablonun hemen üstünde, sağda.
        * Footer'daki "X–Y / Z kayıt" sayacından farklı: bu görsel olarak daha
        * büyük ve göze çarpan bir "Toplam: Z kayıt" özeti. Tablo üstüne sabit
        * yer alır, scroll'a tabi değildir. unknownTotal'da "Z+" gösterir
        * (eski backend cevabı). */}
      {!isManual && data?.summary && !loading && (
        <div className="flex items-center justify-between mb-1.5 px-0.5">
          <p className="text-[11px] text-neutral-500 uppercase tracking-wider">
            Sonuçlar
          </p>
          <div className="flex items-center gap-1.5 text-xs">
            <Icon name="list" className="w-3.5 h-3.5 text-neutral-400" />
            <span className="text-neutral-500">Toplam:</span>
            <span className="font-semibold text-neutral-900 tabular-nums">
              {pagination.total.toLocaleString('tr-TR')}
              {pagination.unknownTotal && '+'}
            </span>
            <span className="text-neutral-500">kayıt</span>
            {/* Sayfa bilgisi — birden fazla sayfa varsa minik chip */}
            {showPagingControls && (
              <span className="ml-2 px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-600 text-[10px]">
                Sayfa {pagination.page} / {pagination.pages ?? '?'}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Yükleniyor */}
      {loading && (
        <div className="py-12 text-center text-neutral-400">
          <Icon name="loader" className="w-5 h-5 mx-auto animate-spin" />
          <p className="text-xs mt-2">Detay yükleniyor…</p>
        </div>
      )}

      {/* Hata */}
      {!loading && error && (
        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* CH tablosu — MANUAL hariç */}
      {!loading && !error && data && !isManual && data.columns && data.columns.length > 0 && (
        <div className="border border-neutral-200 rounded-lg overflow-hidden">
          <div className="max-h-[400px] overflow-auto">
            <table className="w-full text-xs">
              <thead className="bg-neutral-50 sticky top-0 z-10">
                <tr className="text-left text-[11px] font-semibold text-neutral-700 border-b border-neutral-200">
                  {data.columns.map((c) => (
                    <th key={c.name} className="px-2 py-1.5 whitespace-nowrap">
                      <div>{c.name}</div>
                      <div className="text-[9px] text-neutral-400 font-normal">{c.type}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && (
                  <tr>
                    <td colSpan={data.columns.length} className="px-3 py-6 text-center text-neutral-400">
                      Bu kullanıcı için filtreye uyan satır yok.
                    </td>
                  </tr>
                )}
                {data.rows.map((r, idx) => (
                  <tr key={idx} className="border-b border-neutral-100 hover:bg-neutral-50">
                    {data.columns.map((c) => (
                      <td key={c.name} className="px-2 py-1 whitespace-nowrap tabular-nums">
                        {fmtCell(r[c.name])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Debug bölümü — boş satır geldiğinde admin neden boş anlasın diye
        * backend'in oluşturduğu SQL'i, WHERE clause'u ve parametreleri gösterir.
        * Sadece data.debug varsa render edilir (eski backend sürümleri için
        * graceful fallback). <details> ile collapsible — varsayılan kapalı.
        * Boş satır durumunda admin direkt "ne sorgulanmış?" görür ve bug'ı
        * (yanlış tablo, eksik kolon, kötü filter) tespit eder. */}
      {!loading && !error && data?.debug && (
        <details className="mt-3 group">
          <summary className="cursor-pointer text-xs text-neutral-500 hover:text-neutral-700 select-none flex items-center gap-1.5">
            <Icon name="code" className="w-3.5 h-3.5" />
            <span>Debug — Üretilen SQL &amp; parametreler</span>
            <span className="text-[10px] text-neutral-400 ml-1 group-open:hidden">(göster)</span>
            <span className="text-[10px] text-neutral-400 ml-1 hidden group-open:inline">(gizle)</span>
          </summary>
          <div className="mt-2 space-y-2">
            <div>
              <p className="text-[10px] uppercase font-semibold text-neutral-500 mb-1">SQL</p>
              <pre className="text-[11px] bg-neutral-900 text-neutral-100 p-3 rounded-md overflow-auto whitespace-pre-wrap break-all">
                {data.debug.sql}
              </pre>
            </div>
            <div>
              <p className="text-[10px] uppercase font-semibold text-neutral-500 mb-1">WHERE clause</p>
              <pre className="text-[11px] bg-neutral-50 border border-neutral-200 text-neutral-800 p-2 rounded-md overflow-auto whitespace-pre-wrap break-all font-mono">
                {data.debug.where_clause}
              </pre>
            </div>
            <div>
              <p className="text-[10px] uppercase font-semibold text-neutral-500 mb-1">Parametreler</p>
              <pre className="text-[11px] bg-neutral-50 border border-neutral-200 text-neutral-800 p-2 rounded-md overflow-auto whitespace-pre-wrap break-all font-mono">
                {JSON.stringify(data.debug.where_params, null, 2)}
              </pre>
            </div>
          </div>
        </details>
      )}
    </Modal>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[10px] text-neutral-500 uppercase">{label}</p>
      <p className="text-sm font-semibold text-neutral-900">
        {value == null
          ? <span className="text-neutral-300">—</span>
          : Number(value).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </p>
    </div>
  );
}

/**
 * FilterChipGroup — Dahil ya da Hariç filtre setini başlık + renkli chip
 * listesi olarak gösterir. Admin bir bakışta hangi filtre setinin ne olduğunu
 * görür. Boş filtre listesi için tip-belirten metin gösterir.
 *
 * Props:
 *   label      — başlık (örn. "Dahil Etme Filtreleri")
 *   icon       — Icon component name (plus-circle / minus-circle)
 *   color      — Badge rengi (green | red | blue)
 *   filters    — backend'ten gelen filter objelerinin array'i
 *   emptyText  — filters boşsa gösterilecek metin
 *   hideIfEmpty — true ise filter yoksa hiç render etme (default true)
 */
function FilterChipGroup({ label, icon, color, filters, emptyText, hideIfEmpty = true }) {
  const isEmpty = !Array.isArray(filters) || filters.length === 0;
  if (isEmpty && hideIfEmpty) return null;

  return (
    <div className="flex items-start gap-2">
      <div className="flex items-center gap-1 text-neutral-600 min-w-[160px] pt-0.5">
        <Icon name={icon} className={`w-3.5 h-3.5 ${
          color === 'green' ? 'text-emerald-600' :
          color === 'red'   ? 'text-rose-600'    :
          'text-blue-600'
        }`} />
        <span className="font-medium">{label}:</span>
      </div>
      <div className="flex-1 min-w-0">
        {isEmpty ? (
          <span className="text-neutral-400 italic text-[11px]">{emptyText}</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {filters.map((f, i) => (
              <Badge
                key={i}
                color={color}
                size="sm"
                className="!whitespace-normal break-all max-w-full text-left"
              >
                <span className="font-mono">
                  {f.column} {f.op} {
                    Array.isArray(f.value)
                      ? `[${f.value.join(', ')}]`
                      : String(f.value)
                  }
                </span>
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
